import axios, { AxiosResponse, AxiosError } from 'axios';
import { currentConfig } from '@/config';

// Auth failure callback - will be set by AuthContext
let authFailureCallback: (() => void) | null = null;

const apiClient = axios.create({
  baseURL: currentConfig.apiBaseUrl,
  withCredentials: true,
});

// Separate axios instance for refresh calls (no interceptors to avoid loops)
const refreshClient = axios.create({
  baseURL: currentConfig.apiBaseUrl,
  withCredentials: true,
});

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any, token?: string) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve();
    }
  });

  failedQueue = [];
};

apiClient.interceptors.request.use((config) => {
  // Add timezone header to every request
  if (typeof Intl !== 'undefined' && Intl.DateTimeFormat) {
    config.headers['X-Timezone'] = Intl.DateTimeFormat().resolvedOptions().timeZone;
  }
  return config;
});

apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as any;

    // Check if error is 401 and we haven't already tried to refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      console.log('🔒 401 Error detected, attempting token refresh...');

      if (isRefreshing) {
        console.log('⏳ Already refreshing, queueing request...');
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => {
          return apiClient(originalRequest);
        }).catch((err) => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        console.log('🔄 Calling refresh endpoint...');
        // Call refresh endpoint using separate client (no interceptors)
        await refreshClient.post('/api/auth/refresh');

        console.log('✅ Refresh successful, retrying requests...');
        // Refresh successful, retry original request and process queue
        processQueue(null);
        return apiClient(originalRequest);
      } catch (refreshError) {
        console.log('❌ Refresh failed, triggering auth failure...', refreshError);
        // Refresh failed, process queue with error and redirect to login
        processQueue(refreshError);

        // Clear any auth state and redirect to login
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// Function to set auth failure callback from AuthContext
export const setAuthFailureCallback = (callback: () => void) => {
  authFailureCallback = callback;
};

export default apiClient;